"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Clock3,
  Link2,
  Loader2,
  Plus,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { UsersLayout } from "@/components/pages/users-layout";
import { UsersRowMenu } from "@/components/users-row-menu";
import { Card, Pill } from "@/components/pulse-shell";
import { Select } from "@/components/ui/select";
import { api, ApiError, type PlatformRole, type PlatformUser } from "@/lib/api";
import { useAuthProfile } from "@/lib/auth-context";
import {
  displayName,
  formatRelativeActive,
  initialsFromName,
  statusLabel,
  statusTone,
} from "@/lib/users-format";
import { validateAddUserForm, validateEditUserForm } from "@/lib/users-nav";
import { trapFocus } from "@/lib/users-focus-trap";
import { formatApiError } from "@/lib/users-rbac";

function hasPerm(perms: string[] | undefined, key: string) {
  return Boolean(perms?.includes(key));
}

function statusFromParam(raw: string | null): string {
  if (!raw) return "All statuses";
  const u = raw.toUpperCase();
  if (u === "ACTIVE") return "Active";
  if (u === "INVITED") return "Invited";
  if (u === "SUSPENDED") return "Suspended";
  return "All statuses";
}

export function UsersAllPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const me = useAuthProfile();
  const perms = me?.permissions || [];
  const canManage = hasPerm(perms, "users.manage");
  const canAssignRoles = hasPerm(perms, "users.assign_roles") || canManage;
  const canSuspend = hasPerm(perms, "users.suspend");
  const canRemove = hasPerm(perms, "users.remove");

  const [q, setQ] = useState(() => searchParams.get("q") || "");
  const [status, setStatus] = useState(() => statusFromParam(searchParams.get("status")));
  const [roleId, setRoleId] = useState(() => searchParams.get("roleId") || "All roles");
  const [roles, setRoles] = useState<PlatformRole[]>([]);
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(() => {
    const n = Number(searchParams.get("limit") || 10);
    return [10, 25, 50].includes(n) ? n : 10;
  });
  const [offset, setOffset] = useState(() => Math.max(0, Number(searchParams.get("offset") || 0) || 0));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [bulkSummary, setBulkSummary] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState<PlatformUser | null>(null);
  const [advOpen, setAdvOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const searchTimer = useRef<number | null>(null);
  const [searchInput, setSearchInput] = useState(() => searchParams.get("q") || "");
  const skipUrlWrite = useRef(true);

  const loadRoles = useCallback(async () => {
    const out = await api.roles.list({ limit: 100 });
    setRoles(out.roles || []);
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const statusParam =
        status === "All statuses" ? undefined : status.toUpperCase();
      const roleParam = roleId === "All roles" ? undefined : roleId;
      const out = await api.users.list({
        q: q || undefined,
        status: statusParam,
        roleId: roleParam,
        limit,
        offset,
      });
      setUsers(out.users || []);
      setTotal(out.total || 0);
      setSelected(new Set());
    } catch (e) {
      setError(formatApiError(e, "Failed to load users"));
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [q, status, roleId, limit, offset]);

  useEffect(() => {
    void loadRoles().catch(() => undefined);
  }, [loadRoles]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => {
      setOffset(0);
      setQ(searchInput.trim());
    }, 280);
    return () => {
      if (searchTimer.current) window.clearTimeout(searchTimer.current);
    };
  }, [searchInput]);

  useEffect(() => {
    if (skipUrlWrite.current) {
      skipUrlWrite.current = false;
      return;
    }
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (status !== "All statuses") next.set("status", status.toUpperCase());
    if (roleId !== "All roles") next.set("roleId", roleId);
    if (limit !== 10) next.set("limit", String(limit));
    if (offset > 0) next.set("offset", String(offset));
    setSearchParams(next, { replace: true });
  }, [q, status, roleId, limit, offset, setSearchParams]);

  const page = Math.floor(offset / limit) + 1;
  const pageCount = Math.max(1, Math.ceil(total / limit));
  const rangeLabel =
    total === 0
      ? "0 of 0"
      : `${offset + 1}-${Math.min(offset + users.length, total)} of ${total}`;

  const roleOptions = useMemo(
    () => [
      { value: "All roles", label: "All roles" },
      ...roles.map((r) => ({ value: r.roleId, label: r.name })),
    ],
    [roles],
  );

  function toggleAll() {
    if (selected.size === users.length) setSelected(new Set());
    else setSelected(new Set(users.map((u) => u.userId)));
  }

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function runAction(userId: string, action: string) {
    setBusyId(userId);
    setMenuFor(null);
    setError("");
    try {
      if (action === "resend") await api.users.resendInvitation(userId);
      else if (action === "revoke") await api.users.revokeInvitation(userId);
      else if (action === "suspend") await api.users.suspend(userId);
      else if (action === "reactivate") await api.users.reactivate(userId);
      else if (action === "reset") await api.users.passwordReset(userId);
      else if (action === "remove") {
        if (!window.confirm("Remove this platform user? This cannot be undone.")) return;
        await api.users.remove(userId);
      }
      await loadUsers();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusyId(null);
    }
  }

  async function bulkSuspend() {
    const ids = [...selected];
    let ok = 0;
    let failed = 0;
    const failures: string[] = [];
    for (const id of ids) {
      try {
        await api.users.suspend(id);
        ok += 1;
      } catch (e) {
        failed += 1;
        const u = users.find((x) => x.userId === id);
        const label = u ? displayName(u.firstName, u.lastName, u.email) : id;
        failures.push(`${label}: ${e instanceof Error ? e.message : "failed"}`);
      }
    }
    setBulkSummary(
      failed === 0
        ? `Suspended ${ok} user${ok === 1 ? "" : "s"}.`
        : `Suspended ${ok}, failed ${failed}. ${failures.slice(0, 3).join("; ")}`,
    );
    setError(failed > 0 ? failures.slice(0, 5).join("; ") : "");
    await loadUsers();
  }

  return (
    <UsersLayout>
      <Card
        className="users-card"
        title="All users"
        kicker={`${total} user${total === 1 ? "" : "s"}`}
        action={
          canManage ? (
            <button type="button" className="btn primary" onClick={() => setAddOpen(true)}>
              <Plus size={16} /> Add user
            </button>
          ) : null
        }
      >
        <div className="users-toolbar">
          <label className="users-search">
            <Search size={15} />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name or email"
              aria-label="Search by name or email"
            />
          </label>
          <Select
            searchable={false}
            ariaLabel="Status filter"
            value={status}
            onChange={(v) => {
              setOffset(0);
              setStatus(v || "All statuses");
            }}
            options={["All statuses", "Active", "Invited", "Suspended"].map((x) => ({
              value: x,
              label: x,
            }))}
          />
          <Select
            searchable
            ariaLabel="Role filter"
            value={roleId}
            onChange={(v) => {
              setOffset(0);
              setRoleId(v || "All roles");
            }}
            options={roleOptions}
          />
          <div className="users-adv-wrap">
            <button
              type="button"
              className="icon-button"
              aria-label="Advanced filters"
              aria-expanded={advOpen}
              onClick={() => setAdvOpen((o) => !o)}
            >
              <SlidersHorizontal size={16} />
            </button>
            {advOpen && (
              <div className="users-adv-popover" role="dialog" aria-label="Advanced filters">
                <div className="mini-label">Rows per page</div>
                <Select
                  searchable={false}
                  ariaLabel="Rows per page"
                  value={String(limit)}
                  onChange={(v) => {
                    setOffset(0);
                    setLimit(Number(v || 10));
                  }}
                  options={["10", "25", "50"].map((x) => ({ value: x, label: x }))}
                />
                <button type="button" className="btn ghost" onClick={() => setAdvOpen(false)}>
                  Done
                </button>
              </div>
            )}
          </div>
        </div>

        {selected.size > 0 && (
          <div className="users-bulk-bar" role="region" aria-label="Bulk actions">
            <span>{selected.size} selected</span>
            {canSuspend && (
              <button type="button" className="btn ghost" onClick={() => void bulkSuspend()}>
                Suspend
              </button>
            )}
            <button type="button" className="btn ghost" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          </div>
        )}

        {bulkSummary && (
          <div className="users-bulk-summary" role="status">
            {bulkSummary}
            <button type="button" className="btn ghost" onClick={() => setBulkSummary("")}>
              Dismiss
            </button>
          </div>
        )}

        {error && (
          <div className="users-error" role="alert">
            {error}
          </div>
        )}

        <div className="users-table-wrap">
          <table className="users-table">
            <thead>
              <tr>
                <th scope="col">
                  <input
                    type="checkbox"
                    aria-label="Select all on page"
                    checked={users.length > 0 && selected.size === users.length}
                    onChange={toggleAll}
                  />
                </th>
                <th scope="col">User</th>
                <th scope="col">Status</th>
                <th scope="col">Role</th>
                <th scope="col">Security</th>
                <th scope="col">Added by</th>
                <th scope="col">Last active</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="users-empty">
                    <Loader2 className="spin" size={18} /> Loading users…
                  </td>
                </tr>
              )}
              {!loading && users.length === 0 && (
                <tr>
                  <td colSpan={8} className="users-empty">
                    No users match these filters.
                  </td>
                </tr>
              )}
              {!loading &&
                users.map((u) => {
                  const name = displayName(u.firstName, u.lastName, u.email);
                  const self = u.userId === me?.userId || u.email === me?.email;
                  return (
                    <tr key={u.userId}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Select ${name}`}
                          checked={selected.has(u.userId)}
                          onChange={() => toggleOne(u.userId)}
                        />
                      </td>
                      <td>
                        <div className="users-person">
                          <span className="users-avatar" aria-hidden>
                            {initialsFromName(u.firstName, u.lastName, u.email)}
                          </span>
                          <span className="users-person-meta">
                            {canManage || canAssignRoles ? (
                              <button
                                type="button"
                                className="users-name-link"
                                title={name}
                                onClick={() => setEditUser(u)}
                              >
                                {name}
                              </button>
                            ) : (
                              <b title={name}>{name}</b>
                            )}
                            <span title={u.email}>{u.email}</span>
                          </span>
                        </div>
                      </td>
                      <td>
                        <Pill tone={statusTone(u.status)} className="users-status-pill">
                          <i className={`users-status-dot tone-${statusTone(u.status)}`} />
                          {statusLabel(u.status)}
                        </Pill>
                      </td>
                      <td>{u.roleName}</td>
                      <td>
                        {u.security?.kind === "invitation" ? (
                          <span className="users-security muted">
                            <Clock3 size={14} /> {u.security.label}
                          </span>
                        ) : (
                          <span
                            className={`users-security ${u.security?.mfaEnabled ? "ok" : "off"}`}
                          >
                            <ShieldCheck size={14} /> {u.security?.label || "MFA off"}
                          </span>
                        )}
                      </td>
                      <td title={u.addedBy}>{u.addedBy || "System"}</td>
                      <td>{formatRelativeActive(u.lastActive)}</td>
                      <td className="users-row-actions">
                        {u.status === "INVITED" && canManage && (
                          <button
                            type="button"
                            className="btn ghost small"
                            disabled={busyId === u.userId}
                            onClick={() => void runAction(u.userId, "resend")}
                          >
                            Resend
                          </button>
                        )}
                        <UsersRowMenu
                          label={`Actions for ${name}`}
                          open={menuFor === u.userId}
                          onOpenChange={(open) => setMenuFor(open ? u.userId : null)}
                          items={[
                            ...(canManage || canAssignRoles
                              ? [
                                  {
                                    key: "edit",
                                    label: "Edit user",
                                    onSelect: () => setEditUser(u),
                                  },
                                ]
                              : []),
                            ...(u.status === "ACTIVE" && canManage
                              ? [
                                  {
                                    key: "reset",
                                    label: "Send password reset",
                                    onSelect: () => void runAction(u.userId, "reset"),
                                  },
                                ]
                              : []),
                            ...(u.status === "INVITED" && canManage
                              ? [
                                  {
                                    key: "revoke",
                                    label: "Revoke invitation",
                                    onSelect: () => void runAction(u.userId, "revoke"),
                                  },
                                ]
                              : []),
                            ...(u.status === "ACTIVE" && canSuspend && !self
                              ? [
                                  {
                                    key: "suspend",
                                    label: "Suspend",
                                    onSelect: () => void runAction(u.userId, "suspend"),
                                  },
                                ]
                              : []),
                            ...(u.status === "SUSPENDED" && canSuspend
                              ? [
                                  {
                                    key: "reactivate",
                                    label: "Reactivate",
                                    onSelect: () => void runAction(u.userId, "reactivate"),
                                  },
                                ]
                              : []),
                            ...(canRemove && !self
                              ? [
                                  {
                                    key: "remove",
                                    label: "Remove",
                                    danger: true,
                                    onSelect: () => void runAction(u.userId, "remove"),
                                  },
                                ]
                              : []),
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        <div className="users-pagination">
          <span>{rangeLabel}</span>
          <div className="users-pagination-right">
            <span className="muted">Rows per page</span>
            <Select
              size="sm"
              searchable={false}
              ariaLabel="Rows per page"
              value={String(limit)}
              onChange={(v) => {
                setOffset(0);
                setLimit(Number(v || 10));
              }}
              options={["10", "25", "50"].map((x) => ({ value: x, label: x }))}
            />
            <button
              type="button"
              className="icon-button"
              disabled={page <= 1}
              aria-label="Previous page"
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              ‹
            </button>
            <span className="users-page-num">{page}</span>
            <button
              type="button"
              className="icon-button"
              disabled={page >= pageCount}
              aria-label="Next page"
              onClick={() => setOffset(offset + limit)}
            >
              ›
            </button>
          </div>
        </div>
      </Card>

      {addOpen && (
        <AddUserModal
          roles={roles}
          onClose={() => setAddOpen(false)}
          onCreated={async () => {
            setAddOpen(false);
            setOffset(0);
            await loadUsers();
          }}
        />
      )}
      {editUser && (
        <EditUserModal
          user={editUser}
          roles={roles}
          canAssignRoles={canAssignRoles}
          onClose={() => setEditUser(null)}
          onSaved={async () => {
            setEditUser(null);
            await loadUsers();
          }}
        />
      )}
    </UsersLayout>
  );
}

function AddUserModal({
  roles,
  onClose,
  onCreated,
}: {
  roles: PlatformRole[];
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const me = useAuthProfile();
  const canGrantAdmin = hasPerm(me?.permissions, "users.grant_admin");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState(roles.find((r) => r.name === "Operator")?.roleId || "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const firstRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const release = dialogRef.current ? trapFocus(dialogRef.current) : () => undefined;
    return () => {
      window.removeEventListener("keydown", onKey);
      release();
    };
  }, [onClose]);

  const roleOpts = roles
    .filter((r) => canGrantAdmin || r.name !== "Administrator")
    .map((r) => ({ value: r.roleId, label: r.name }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    const validationError = validateAddUserForm({ firstName, lastName, email, roleId });
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    try {
      await api.users.create({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        roleId,
      });
      await onCreated();
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : "Failed to add user");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="users-modal-overlay" role="presentation" onMouseDown={onClose}>
      <div
        ref={dialogRef}
        className="users-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-user-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="users-modal-head">
          <div>
            <h2 id="add-user-title">Add user</h2>
            <p>Create a workspace account and send an invitation to sign in.</p>
          </div>
          <button type="button" className="icon-button" aria-label="Close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <form onSubmit={(e) => void submit(e)}>
          <div className="users-modal-grid">
            <label>
              <span>
                First name <span aria-hidden>*</span>
              </span>
              <input
                ref={firstRef}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoComplete="given-name"
              />
            </label>
            <label>
              <span>
                Last name <span aria-hidden>*</span>
              </span>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                autoComplete="family-name"
              />
            </label>
          </div>
          <label className="users-modal-full">
            <span>
              Work email <span aria-hidden>*</span>
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </label>
          <label className="users-modal-full">
            <span>
              Role <span aria-hidden>*</span>
            </span>
            <Select
              searchable
              ariaLabel="Role"
              value={roleId}
              onChange={(v) => setRoleId(v || "")}
              options={roleOpts}
            />
          </label>
          <p className="users-invite-note">
            An invitation email is sent automatically. The user will appear as Invited until they
            accept.
          </p>
          <div className="users-info-banner">
            <Link2 size={16} />
            <span>
              You can resend or revoke the invitation from the Users list.
            </span>
          </div>
          {error && (
            <div className="users-error" role="alert">
              {error}
            </div>
          )}
          <div className="users-modal-foot">
            <button type="button" className="btn ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={submitting}>
              {submitting ? "Adding…" : "Add user"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditUserModal({
  user,
  roles,
  canAssignRoles,
  onClose,
  onSaved,
}: {
  user: PlatformUser;
  roles: PlatformRole[];
  canAssignRoles: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const me = useAuthProfile();
  const canGrantAdmin = hasPerm(me?.permissions, "users.grant_admin");
  const [firstName, setFirstName] = useState(user.firstName || "");
  const [lastName, setLastName] = useState(user.lastName || "");
  const [roleId, setRoleId] = useState(user.roleId || "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const firstRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const release = dialogRef.current ? trapFocus(dialogRef.current) : () => undefined;
    return () => {
      window.removeEventListener("keydown", onKey);
      release();
    };
  }, [onClose]);

  const roleOpts = roles
    .filter((r) => canGrantAdmin || r.name !== "Administrator" || r.roleId === user.roleId)
    .map((r) => ({ value: r.roleId, label: r.name }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    const validationError = validateEditUserForm({ firstName, lastName, roleId });
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    try {
      const body: { firstName: string; lastName: string; roleId?: string; version?: number } = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        version: user.version,
      };
      if (canAssignRoles && roleId !== user.roleId) {
        body.roleId = roleId;
      }
      await api.users.update(user.userId, body);
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : "Failed to save user");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="users-modal-overlay" role="presentation" onMouseDown={onClose}>
      <div
        ref={dialogRef}
        className="users-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-user-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="users-modal-head">
          <h2 id="edit-user-title">Edit user</h2>
          <button type="button" className="icon-button" aria-label="Close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <form className="users-modal-form" onSubmit={(e) => void submit(e)}>
          <p className="users-invite-note" style={{ marginTop: 0 }}>
            {user.email}
          </p>
          <div className="users-modal-grid">
            <label>
              <span>
                First name <span aria-hidden>*</span>
              </span>
              <input
                ref={firstRef}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoComplete="given-name"
              />
            </label>
            <label>
              <span>
                Last name <span aria-hidden>*</span>
              </span>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                autoComplete="family-name"
              />
            </label>
          </div>
          <label className="users-modal-full">
            <span>
              Role <span aria-hidden>*</span>
            </span>
            <Select
              searchable
              ariaLabel="Role"
              value={roleId}
              onChange={(v) => setRoleId(v || "")}
              options={roleOpts}
              disabled={!canAssignRoles}
            />
          </label>
          {error && (
            <div className="users-error" role="alert">
              {error}
            </div>
          )}
          <div className="users-modal-foot">
            <button type="button" className="btn ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={submitting}>
              {submitting ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
